import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Spinner } from 'src/common/components/Spinner';
import { Modal } from 'src/common/components/Modal';
import { Button } from 'src/common/components/buttons/Button';
import newOwnerIllu from 'src/assets/illu_new-owner.svg';
import { SelectNative } from 'src/common/components/inputs/SelectNative/SelectNative';
import toast from 'src/common/toast';
import { useAdminCompanyUsersQuery } from 'src/admin/queries';
import { updateCompany } from 'src/admin/apiCompanies';

import type { OwnerPopulatedCompany } from 'shared';

type Props = {
  company: OwnerPopulatedCompany;
  isOpen: boolean;
  onClose: () => void;
};

export const CompanyNewOwnerModal = ({ company, isOpen, onClose }: Props) => {
  const { t } = useTranslation(['company', 'common']);
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<{ newOwner: string }>({ defaultValues: { newOwner: '' } });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: companyMembers, isPending } = useAdminCompanyUsersQuery(
    company._id,
  );

  const potentialNewOwners = companyMembers.filter(
    (u) => u._id !== company.owner._id,
  );

  async function handleTransfer({ newOwner }: { newOwner: string }) {
    try {
      setIsSubmitting(true);
      await updateCompany({ owner: newOwner }, company._id);
      await queryClient.invalidateQueries({
        queryKey: ['admin-companies'],
      });
      toast.success(t('company:transferOwner.success'));
      onClose();
    } catch (e) {
      toast.error(t('company:transferOwner.failure'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="w-[680px]">
      <Modal.Header onClose={onClose}>
        {t('company:transferOwner.title')}
      </Modal.Header>
      <Modal.Body>
        <form
          className="mt-8 flex flex-col items-center gap-6"
          onSubmit={handleSubmit(handleTransfer)}
        >
          <div className="ml-4 flex gap-9">
            <img src={newOwnerIllu} alt="" />
            <div className="flex w-full flex-col gap-4">
              <p className="text-shuttleGray-600">
                <Trans ns="company" i18nKey="transferOwner.text" />
              </p>
              {isPending || isSubmitting ? (
                <Spinner />
              ) : (
                <SelectNative
                  label={t('company:transferOwner.newOwner')}
                  required
                  {...register('newOwner')}
                >
                  <option value="" disabled hidden>
                    {t('company:transferOwner.choose')}
                  </option>
                  {potentialNewOwners.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.name} {member.surname}
                    </option>
                  ))}
                </SelectNative>
              )}
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button
              variant="tertiary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('common:button.abort')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!isDirty}
              loading={isSubmitting}
            >
              {t('common:button.save')}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
